import React, { useState, useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast, Flip } from 'react-toastify';
import { setSelectedLanguage } from '@modules/subtitle';
import { useTranslation } from 'react-i18next';

import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import TranslateIcon from '@mui/icons-material/Translate';

import langListData from '@resources/subLangList.json';
import api from '@utils/axiosInstance';

function TranslateContent({
    availableSubtitleLanguages,
    setAvailableSubtitleLanguages,
    setIsTranslateModalOpen
}) {
    const [allLanguages, setAllLanguages] = useState([]);
    const [selectedSubtitleLanguage, setSelectedSubtitleLanguage] = useState(null);
    const [isSubtitleTranslationInProgress, setIsSubtitleTranslationInProgress] = useState(false);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { userToken, organizationData } = useSelector(state => state.organization);
    const { selectedLanguage } = useSelector(state => state.subtitle);
    const { uuid } = useParams();

    useEffect(() => {
        if (langListData.languages.length > 0) {
            const filteredLanguages = langListData.languages.filter(lang => lang.lang !== selectedLanguage.lang);
            setAllLanguages(filteredLanguages);
        }
    }, [langListData, selectedLanguage]);

    const handleLanguageChange = (lang) => {
        setSelectedSubtitleLanguage(lang);
        const selectedLangObject = allLanguages.find(lang => lang.lang === lang);
        if (selectedLangObject) {
            dispatch(setSelectedLanguage(selectedLangObject));
        }
    };

    const handleCreateSubtitle = () => {
        if (!selectedSubtitleLanguage) {
            toast.error(t("vod.manage.subtitle_tab.select_language"));
            return;
        }

        setIsSubtitleTranslationInProgress(true);
        const toastId = toast.loading(t("vod.manage.subtitle_tab.creating_subtitle"), {
            position: "top-center",
            autoClose: 3000,
            className: 'rotateY animated',
            transition: Flip
        });

        api({
            method: 'post',
            url: '/subtitle/translate',
            data: {
                tk: userToken,
                uuid,
                ogc: organizationData.organization_code,
                fromLang: selectedLanguage.lang,
                toLang: selectedSubtitleLanguage
            }
        })
            .then(response => {
                if (response.data.code === 100) {
                    toast.update(toastId, {
                        render: t("vod.manage.subtitle_tab.subtitle_translation_complete"),
                        type: "success",
                        isLoading: false,
                        autoClose: 3000
                    });
                    setAvailableSubtitleLanguages([
                        ...availableSubtitleLanguages,
                        {
                            "uuid": uuid,
                            "lang": selectedSubtitleLanguage,
                            "state": 1
                        }])
                    setIsTranslateModalOpen(false);
                } else {
                    throw new Error(response.data.message || t("vod.manage.subtitle_tab.subtitle_translation_failed"));
                }
            })
            .catch(error => {
                toast.update(toastId, {
                    render: t("common.error_occurred"),
                    type: "error",
                    isLoading: false,
                    autoClose: 3000
                });
            })
            .finally(() => {
                setIsSubtitleTranslationInProgress(false);
            });
    };

    return (
        <Box className="translate-content">
            <Box className="translate-content-container">
                <Typography variant="h6" className="translate-content-title">{t("vod.manage.subtitle_tab.subtitle_translation")}</Typography>
                <Typography variant="body1" className="translate-content-description">
                    {t("vod.manage.subtitle_tab.select_language_prompt")}
                </Typography>
                <FormControl variant="outlined" className="translate-content-form">
                    {allLanguages.length > 0 ? (
                        <Autocomplete
                            options={allLanguages}
                            getOptionLabel={(option) => t(`languages.${option.lang}`)}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    handleLanguageChange(newValue.lang);
                                }
                            }}
                            noOptionsText={t("vod.manage.subtitle_tab.no_matching_data")}
                            className="translate-content-autocomplete"
                            renderInput={(params) => (
                                <Box>
                                    <TextField
                                        {...params}
                                        label={t("vod.manage.subtitle_tab.select_subtitle_language")}
                                        className="translate-content-textfield"
                                        InputProps={{ ...params.InputProps, className: "translate-content-input" }}
                                        InputLabelProps={{ ...params.InputLabelProps, className: "translate-content-label" }}
                                    />
                                </Box>
                            )}
                        />
                    ) : (
                        <></>
                    )}
                </FormControl>
                {selectedSubtitleLanguage ? (
                    <Box className="translate-language-box">
                        <Box className="translate-language-text">
                            <Typography variant="body1">{t(`languages.${selectedLanguage.lang}`)}</Typography>
                            <Typography variant="body1">→</Typography>
                            <Typography variant="body1">{t(`languages.${selectedSubtitleLanguage}`)}</Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box className="translate-content-spacer" />
                )}
                <Box className="translate-content-actions">
                    <Button
                        className="btn-sub-edit"
                        variant="contained"
                        startIcon={<TranslateIcon />}
                        color="success"
                        onClick={() => handleCreateSubtitle()}
                        disabled={isSubtitleTranslationInProgress || !selectedSubtitleLanguage}
                        fullWidth
                    >
                        {t("vod.manage.subtitle_tab.translate_existing_subtitle")}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default TranslateContent;